import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { networkApi } from '../../services/api';
import wsService from '../../services/websocket';
import StepUpDialog from '../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import './AdminPacketStats.css';

const extractData = (response) => response?.data?.data ?? response?.data ?? {};

const normalizeEntries = (value) => {
  const entries = Array.isArray(value) ? value : [];
  return entries
    .map((entry) => ({
      ip: String(entry?.ip || entry?.ipAddress || entry?.address || '').trim(),
      count: Number(entry?.count || entry?.packets || 0),
    }))
    .filter((entry) => entry.ip && Number.isFinite(entry.count))
    .sort((left, right) => right.count - left.count);
};

const normalizeWindow = (value, fallbackSeconds) => {
  const entries = normalizeEntries(value?.topIps || value?.top10 || value?.entries);
  return {
    entries,
    totalPackets: Number(value?.totalPackets ?? entries.reduce((sum, entry) => sum + entry.count, 0)),
    uniqueIps: Number(value?.uniqueIps ?? entries.length),
    elapsedSeconds: Number(value?.elapsedSeconds ?? 0),
    windowStart: value?.windowStart || value?.windowStartedAt || null,
    windowEnd: value?.windowEnd || null,
    windowSeconds: Number(value?.windowSeconds || fallbackSeconds),
  };
};

const normalizeSnapshot = (payload) => ({
  tenSeconds: normalizeWindow(payload?.tenSeconds, 10),
  oneMinute: normalizeWindow(payload?.oneMinute, 60),
});

const normalizeHistorySnapshots = (payload) => {
  const data = payload?.snapshots || payload?.history || payload;
  return (Array.isArray(data) ? data : [])
    .map((snapshot) => ({
      windowStart: snapshot?.windowStart || snapshot?.bucketStart || snapshot?.windowStartedAt || null,
      windowEnd: snapshot?.windowEnd || null,
      top10: normalizeEntries(snapshot?.top10 || snapshot?.topIps || snapshot?.entries),
      totalPackets: Number(snapshot?.totalPackets || 0),
      uniqueIps: Number(snapshot?.uniqueIps || 0),
    }))
    .filter((snapshot) => snapshot.top10.length > 0 || snapshot.totalPackets > 0);
};

const normalizeHistogram = (payload) => {
  const buckets = payload?.histogram || payload?.buckets || [];
  return (Array.isArray(buckets) ? buckets : [])
    .map((bucket) => ({
      windowStart: bucket?.windowStart || bucket?.bucketStart || null,
      windowEnd: bucket?.windowEnd || null,
      count: Number(bucket?.count || 0),
    }))
    .filter((bucket) => Number.isFinite(bucket.count));
};

const formatNumber = (value) => new Intl.NumberFormat('en-GB').format(Number(value || 0));

const formatTime = (value) => {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const hashIp = (ip) => {
  let hash = 0;
  for (let index = 0; index < ip.length; index += 1) {
    hash = (hash * 31 + ip.charCodeAt(index)) >>> 0;
  }
  return hash;
};

const seriesColor = (ip) => {
  const palette = ['#0f766e', '#2563eb', '#b45309', '#7c3aed', '#be123c', '#15803d', '#0891b2', '#a21caf', '#ca8a04', '#4338ca'];
  return palette[hashIp(ip) % palette.length];
};

const getErrorMessage = (error) => {
  const code = error?.response?.data?.error;
  const message = error?.response?.data?.message || error?.message;
  if (/STEP_UP_REQUIRED|SAL|ASSURANCE/i.test(`${code} ${message}`)) {
    return 'A fresh second factor is required before packet stats can be viewed.';
  }
  return message || 'Packet stats could not be loaded.';
};

const Sparkline = ({ buckets, color = '#2563eb', label = 'Packet history' }) => {
  const values = buckets.map((bucket) => bucket.count);
  const max = Math.max(...values, 1);
  const points = values.length > 0
    ? values.map((value, index) => {
      const x = values.length === 1 ? 100 : (index / (values.length - 1)) * 100;
      const y = 36 - ((value / max) * 32);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    }).join(' ')
    : '';

  return (
    <svg className="packet-stats-sparkline" viewBox="0 0 100 40" role="img" aria-label={label} preserveAspectRatio="none">
      <line x1="0" x2="100" y1="36" y2="36" className="packet-stats-axis" />
      {points ? <polyline points={points} fill="none" stroke={color} strokeWidth="2.5" vectorEffect="non-scaling-stroke" /> : null}
    </svg>
  );
};

const TopTalkersChart = ({ snapshots, range, onRangeChange }) => {
  const topIps = useMemo(() => {
    const totals = new Map();
    snapshots.forEach((snapshot) => {
      snapshot.top10.forEach((entry) => totals.set(entry.ip, (totals.get(entry.ip) || 0) + entry.count));
    });
    return Array.from(totals.entries())
      .sort((left, right) => right[1] - left[1])
      .slice(0, 10)
      .map(([ip]) => ip);
  }, [snapshots]);

  const maxTotal = Math.max(...snapshots.map((snapshot) => snapshot.totalPackets), 1);
  const bars = snapshots.slice(-24);

  return (
    <section className="packet-stats-panel" aria-labelledby="packet-history-heading">
      <div className="packet-stats-panel-header">
        <div>
          <h3 id="packet-history-heading">Top Talkers Over Time</h3>
          <p>Recent one-minute top-10 snapshots.</p>
        </div>
        <div className="packet-stats-segmented" aria-label="Time range">
          {[1, 6, 12, 24].map((hours) => (
            <button key={hours} type="button" className={range === hours ? 'active' : ''} onClick={() => onRangeChange(hours)}>
              {hours}h
            </button>
          ))}
        </div>
      </div>

      <svg className="packet-stats-chart" viewBox="0 0 720 220" role="img" aria-label="Top talkers over time">
        <line x1="44" y1="184" x2="696" y2="184" className="packet-stats-axis" />
        <line x1="44" y1="18" x2="44" y2="184" className="packet-stats-axis" />
        {bars.map((snapshot, snapshotIndex) => {
          const barWidth = Math.max(8, 620 / Math.max(bars.length, 1) - 6);
          const x = 58 + snapshotIndex * (620 / Math.max(bars.length, 1));
          let yOffset = 184;
          const entries = snapshot.top10.filter((entry) => topIps.includes(entry.ip));
          return entries.map((entry) => {
            const height = Math.max(2, (entry.count / maxTotal) * 150);
            yOffset -= height;
            return (
              <rect
                key={`${snapshot.windowStart}-${entry.ip}`}
                x={x}
                y={yOffset}
                width={barWidth}
                height={height}
                fill={seriesColor(entry.ip)}
              >
                <title>{`${entry.ip}: ${formatNumber(entry.count)} packets at ${formatTime(snapshot.windowStart)}`}</title>
              </rect>
            );
          });
        })}
        <text x="44" y="208" className="packet-stats-chart-label">time</text>
        <text x="8" y="24" className="packet-stats-chart-label">pkts</text>
      </svg>

      <div className="packet-stats-legend" aria-label="Chart legend">
        {topIps.length === 0 ? <span>No history yet</span> : topIps.map((ip) => (
          <span key={ip}>
            <i style={{ backgroundColor: seriesColor(ip) }} aria-hidden="true" />
            {ip}
          </span>
        ))}
      </div>
    </section>
  );
};

const CurrentTopTalkersTable = ({ windowData, granularity, onGranularityChange, selectedIp, onSelectIp }) => {
  const entries = windowData.entries.slice(0, 20);
  const maxCount = Math.max(...entries.map((entry) => entry.count), 1);

  return (
    <section className="packet-stats-panel" aria-labelledby="packet-current-heading">
      <div className="packet-stats-panel-header">
        <div>
          <h3 id="packet-current-heading">Current Window</h3>
          <p>{granularity === '10s' ? 'Showing the 10-second window.' : 'Showing the one-minute window.'}</p>
        </div>
        <div className="packet-stats-segmented" aria-label="Granularity">
          <button type="button" className={granularity === '10s' ? 'active' : ''} onClick={() => onGranularityChange('10s')}>10s</button>
          <button type="button" className={granularity === '1m' ? 'active' : ''} onClick={() => onGranularityChange('1m')}>1m</button>
        </div>
      </div>

      <div className="packet-stats-table-wrap">
        <table className="packet-stats-table">
          <thead>
            <tr>
              <th scope="col">Rank</th>
              <th scope="col">IP Address</th>
              <th scope="col">Packets</th>
              <th scope="col">Share</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan="4" className="packet-stats-empty">No broadcast packets detected yet</td>
              </tr>
            ) : entries.map((entry, index) => (
              <tr key={entry.ip} className={selectedIp === entry.ip ? 'selected' : ''}>
                <td>{index + 1}</td>
                <td>
                  <button type="button" className="packet-stats-ip-button" onClick={() => onSelectIp(entry.ip)}>
                    {entry.ip}
                  </button>
                </td>
                <td>{formatNumber(entry.count)}</td>
                <td>
                  <div className="packet-stats-bar-track" aria-label={`${entry.ip} ${entry.count} packets`}>
                    <span
                      className="packet-stats-bar"
                      style={{ width: `${Math.max(3, (entry.count / maxCount) * 100)}%`, backgroundColor: seriesColor(entry.ip) }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="4">
                Total: {formatNumber(windowData.totalPackets)} packets | {formatNumber(windowData.uniqueIps)} unique IPs | {formatNumber(Math.round(windowData.elapsedSeconds))}s elapsed
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
};

const IpDetailPanel = ({ selectedIp, granularity, onGranularityChange, buckets, loading, error }) => {
  const counts = buckets.map((bucket) => bucket.count);
  const total = counts.reduce((sum, value) => sum + value, 0);
  const peak = Math.max(...counts, 0);
  const average = counts.length > 0 ? total / counts.length : 0;

  return (
    <section className="packet-stats-panel" aria-labelledby="packet-detail-heading">
      <div className="packet-stats-panel-header">
        <div>
          <h3 id="packet-detail-heading">IP Detail</h3>
          <p>{selectedIp ? selectedIp : 'Click a row to inspect packet history.'}</p>
        </div>
        <div className="packet-stats-segmented" aria-label="Detail granularity">
          <button type="button" className={granularity === '10s' ? 'active' : ''} onClick={() => onGranularityChange('10s')}>10s</button>
          <button type="button" className={granularity === '1m' ? 'active' : ''} onClick={() => onGranularityChange('1m')}>1m</button>
        </div>
      </div>

      {selectedIp ? (
        <>
          {loading ? <p className="packet-stats-muted">Loading history...</p> : null}
          {error ? <div className="packet-stats-alert">{error}</div> : null}
          <Sparkline buckets={buckets} color={seriesColor(selectedIp)} label={`${selectedIp} packet history`} />
          <div className="packet-stats-metrics">
            <span>Total: {formatNumber(total)} packets</span>
            <span>Peak: {formatNumber(peak)}</span>
            <span>Avg: {average.toFixed(1)}</span>
          </div>
        </>
      ) : (
        <div className="packet-stats-empty">No IP selected</div>
      )}
    </section>
  );
};

const PacketStatsStatusBar = ({ status, lastUpdated }) => (
  <section className="packet-stats-status" aria-label="Packet stats status">
    <span>{formatNumber(status?.totalPacketsCounted)} packets counted</span>
    <span>{formatNumber(status?.uniqueIpsTracked)} unique IPs tracked</span>
    <span>10s history: {formatNumber(status?.history10sSize)}</span>
    <span>1m history: {formatNumber(status?.history1mSize)}</span>
    <span>Top-10: {formatNumber(status?.top10HistorySize)}</span>
    <span>Last update: {formatTime(lastUpdated || status?.lastTallyAt)}</span>
  </section>
);

const AdminPacketStats = ({ networkScope = {} }) => {
  const [snapshot, setSnapshot] = useState(normalizeSnapshot({}));
  const [latestSnapshot, setLatestSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState({});
  const [granularity, setGranularity] = useState('10s');
  const [range, setRange] = useState(1);
  const [paused, setPaused] = useState(false);
  const [connectionState, setConnectionState] = useState(wsService.getConnectionState());
  const [selectedIp, setSelectedIp] = useState('');
  const [detailGranularity, setDetailGranularity] = useState('10s');
  const [detailBuckets, setDetailBuckets] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  const [stepUpRetry, setStepUpRetry] = useState(null);

  const applyCurrent = useCallback((payload) => {
    const normalized = normalizeSnapshot(payload);
    setLatestSnapshot(normalized);
    setSnapshot(normalized);
    setLastUpdated(new Date().toISOString());
  }, []);

  const applyWindow = useCallback((payload, windowKey) => {
    setSnapshot((current) => {
      const next = {
        ...current,
        [windowKey]: normalizeWindow(payload, windowKey === 'tenSeconds' ? 10 : 60),
      };
      setLatestSnapshot(next);
      return next;
    });
    if (windowKey === 'oneMinute') {
      setHistory((current) => [...current.slice(-1439), {
        windowStart: payload?.windowStart || payload?.windowStartedAt || new Date().toISOString(),
        windowEnd: payload?.windowEnd || null,
        top10: normalizeEntries(payload?.top10 || payload?.topIps || payload?.entries),
        totalPackets: Number(payload?.totalPackets || 0),
        uniqueIps: Number(payload?.uniqueIps || 0),
      }]);
    }
    setLastUpdated(new Date().toISOString());
  }, []);

  const handlePacketEvent = useCallback((payload, windowKey = null) => {
    if (paused) {
      setLatestSnapshot((current) => {
        const base = current || snapshot;
        if (!windowKey) return normalizeSnapshot(payload);
        return {
          ...base,
          [windowKey]: normalizeWindow(payload, windowKey === 'tenSeconds' ? 10 : 60),
        };
      });
      return;
    }
    if (!windowKey) {
      applyCurrent(payload);
    } else {
      applyWindow(payload, windowKey);
    }
  }, [applyCurrent, applyWindow, paused, snapshot]);

  const loadPacketStats = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      const [currentResponse, historyResponse, statusResponse] = await Promise.all([
        networkApi.getPacketStatsCurrent({ ...networkScope, topN: 20 }),
        networkApi.getPacketStatsHistoryTop10({ ...networkScope, count: Math.min(range * 60, 1440) }),
        networkApi.getPacketStatsStatus(networkScope),
      ]);
      applyCurrent(extractData(currentResponse));
      setHistory(normalizeHistorySnapshots(extractData(historyResponse)));
      setStatus(extractData(statusResponse));
    } catch (err) {
      const challenge = getStepUpChallengeFromError(err);
      if (challenge) {
        setStepUpChallenge(challenge);
        setStepUpRetry(() => () => loadPacketStats());
        setError('');
        return;
      }
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [applyCurrent, networkScope.ownerOrgId, networkScope.installationUuid, range]);

  useEffect(() => {
    loadPacketStats();
  }, [loadPacketStats]);

  useEffect(() => {
    wsService.connect();
    const requestSubscribe = () => {
      if (wsService.socket?.connected) {
        wsService.socket.emit('subscribe:packet-stats');
      }
    };

    requestSubscribe();
    const subscriptions = [
      wsService.subscribe('connectionChange', ({ state }) => {
        setConnectionState(state);
        if (state === 'connected') requestSubscribe();
      }),
      wsService.subscribe('packet-stats:current', (payload) => handlePacketEvent(payload)),
      wsService.subscribe('packet-stats:10s', (payload) => handlePacketEvent(payload, 'tenSeconds')),
      wsService.subscribe('packet-stats:1m', (payload) => handlePacketEvent(payload, 'oneMinute')),
    ];

    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
    };
  }, [handlePacketEvent]);

  useEffect(() => {
    if (!selectedIp) {
      setDetailBuckets([]);
      setDetailError('');
      return;
    }

    let cancelled = false;
    const loadDetail = async () => {
      try {
        setDetailLoading(true);
        setDetailError('');
        const response = await networkApi.getPacketStatsIpHistory(selectedIp, {
          ...networkScope,
          granularity: detailGranularity,
          windows: detailGranularity === '10s' ? 180 : 60,
        });
        if (!cancelled) {
          setDetailBuckets(normalizeHistogram(extractData(response)));
        }
      } catch (err) {
        if (!cancelled) {
          const challenge = getStepUpChallengeFromError(err);
          if (challenge) {
            setStepUpChallenge(challenge);
            setStepUpRetry(() => () => loadDetail());
            setDetailError('');
            setDetailBuckets([]);
            return;
          }
          setDetailError(getErrorMessage(err));
          setDetailBuckets([]);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [detailGranularity, networkScope.ownerOrgId, networkScope.installationUuid, selectedIp]);

  const displayedWindow = granularity === '10s' ? snapshot.tenSeconds : snapshot.oneMinute;
  const isLive = connectionState === 'connected';

  const closeStepUp = () => {
    setStepUpChallenge(null);
    setStepUpRetry(null);
  };

  const completeStepUp = async () => {
    const retry = stepUpRetry;
    closeStepUp();
    if (retry) {
      await retry();
    }
  };

  const togglePaused = () => {
    setPaused((current) => {
      if (current && latestSnapshot) {
        setSnapshot(latestSnapshot);
        setLastUpdated(new Date().toISOString());
      }
      return !current;
    });
  };

  return (
    <div className="packet-stats" data-testid="packet-stats-page">
      <StepUpDialog
        challenge={stepUpChallenge}
        title="Packet stats need 2FA"
        description="Packet monitoring requires a fresh SAL 4 verification before protected statistics can load."
        onComplete={completeStepUp}
        onCancel={closeStepUp}
      />
      <div className="packet-stats-header">
        <div>
          <h2>Packet Stats</h2>
          <p>Real-time broadcast packet monitoring by source IP.</p>
        </div>
        <div className="packet-stats-actions">
          <span className={`packet-stats-live ${isLive ? 'connected' : 'disconnected'}`} data-testid="packet-stats-live-state">
            <i aria-hidden="true" /> {isLive ? 'Live' : 'Disconnected'}
          </span>
          <button type="button" className="network-secondary-button" onClick={togglePaused}>
            {paused ? 'Resume' : 'Pause'}
          </button>
          <button type="button" className="network-secondary-button" onClick={loadPacketStats}>
            Refresh
          </button>
        </div>
      </div>

      {error ? <div className="packet-stats-alert" data-testid="packet-stats-error">{error}</div> : null}
      {loading ? <div className="packet-stats-muted">Loading packet stats...</div> : null}

      <CurrentTopTalkersTable
        windowData={displayedWindow}
        granularity={granularity}
        onGranularityChange={setGranularity}
        selectedIp={selectedIp}
        onSelectIp={setSelectedIp}
      />
      <TopTalkersChart snapshots={history} range={range} onRangeChange={setRange} />
      <IpDetailPanel
        selectedIp={selectedIp}
        granularity={detailGranularity}
        onGranularityChange={setDetailGranularity}
        buckets={detailBuckets}
        loading={detailLoading}
        error={detailError}
      />
      <PacketStatsStatusBar status={status} lastUpdated={lastUpdated} />
    </div>
  );
};

export default AdminPacketStats;
